// src/components/Layout.js
import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { auth, db } from "../firebase";
import {
  doc,
  getDoc,
  collection,
  getDocs,
  query,
  where,
  orderBy,
} from "firebase/firestore";
import logo from "../assets/river-ranch-logo.png";
import mobileLogo from "../assets/river-ranch-logo-mobile.png";

const COPPER = "#B87333";
const PULL_TRIGGER = 82;
const PULL_MAX = 122;

function initialsFromName(name) {
  const s = String(name || "").trim();
  if (!s) return "";
  const parts = s.split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] || "";
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] : "";
  return (first + last).toUpperCase();
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function isMobileTouchDevice() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }

  return window.matchMedia("(max-width: 767px) and (pointer: coarse)").matches;
}

function Avatar({ name, photoUrl, size = 40 }) {
  const initials = useMemo(() => initialsFromName(name), [name]);
  const px = `${size}px`;

  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt={name ? `${name} avatar` : "avatar"}
        className="rounded-full object-cover border border-zinc-700"
        style={{
          width: px,
          height: px,
          boxShadow: "0 0 0 2px rgba(184,115,51,0.10)",
        }}
        loading="lazy"
      />
    );
  }

  return (
    <div
      className="rounded-full flex items-center justify-center font-bold border select-none list-none"
      style={{
        width: px,
        height: px,
        borderColor: "rgba(184,115,51,0.22)",
        background: "rgba(184,115,51,0.10)",
        color: COPPER,
        fontSize: size >= 44 ? 14 : 12,
        boxShadow: "0 0 0 2px rgba(184,115,51,0.10)",
      }}
      title={name || "Member"}
      aria-hidden="true"
    >
      {initials || "—"}
    </div>
  );
}

function Badge({ children, tone = "neutral" }) {
  const styles =
    tone === "copper"
      ? {
          background: "rgba(184,115,51,0.12)",
          border: "1px solid rgba(184,115,51,0.28)",
          color: COPPER,
        }
      : tone === "green"
      ? {
          background: "rgba(34,197,94,0.12)",
          border: "1px solid rgba(34,197,94,0.25)",
          color: "#86efac",
        }
      : tone === "red"
      ? {
          background: "rgba(239,68,68,0.12)",
          border: "1px solid rgba(239,68,68,0.25)",
          color: "#fca5a5",
        }
      : {
          background: "rgba(255,255,255,0.05)",
          border: "1px solid rgba(255,255,255,0.10)",
          color: "#e4e4e7",
        };

  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold list-none"
      style={styles}
    >
      {children}
    </span>
  );
}

function PullToRefreshIndicator({ pullDistance, isRefreshing }) {
  const progress = clamp(pullDistance / PULL_TRIGGER, 0, 1);
  const ready = pullDistance >= PULL_TRIGGER;

  const ringRadius = 16;
  const ringCircumference = 2 * Math.PI * ringRadius;
  const ringOffset = ringCircumference * (1 - progress);

  const label = isRefreshing
    ? "Refreshing..."
    : ready
    ? "Release to refresh"
    : "Pull to refresh";

  const sublabel = isRefreshing
    ? "Updating current page"
    : ready
    ? "Let go to reload current data"
    : "Swipe down from the top";

  const indicatorTranslateY = -84 + Math.min(pullDistance, 92);
  const ringScale = isRefreshing ? 1 : ready ? 1.06 : 0.96 + progress * 0.08;

  return (
    <div
      className="md:hidden pointer-events-none absolute left-1/2 top-0 z-20"
      style={{
        transform: `translate(-50%, ${indicatorTranslateY}px)`,
        opacity: isRefreshing || pullDistance > 0 ? 1 : 0,
        transition: isRefreshing
          ? "transform 180ms ease, opacity 180ms ease"
          : "opacity 180ms ease",
      }}
      aria-hidden="true"
    >
      <div
        className="rounded-full border px-4 py-2.5 shadow-xl backdrop-blur"
        style={{
          borderColor: ready
            ? "rgba(184,115,51,0.38)"
            : "rgba(184,115,51,0.24)",
          background: ready
            ? "rgba(24,24,27,0.97)"
            : "rgba(24,24,27,0.94)",
          boxShadow: ready
            ? "0 14px 34px rgba(0,0,0,0.34), 0 0 0 1px rgba(184,115,51,0.12), 0 0 18px rgba(184,115,51,0.16)"
            : "0 10px 28px rgba(0,0,0,0.30), 0 0 0 1px rgba(184,115,51,0.08)",
          transition:
            "border-color 160ms ease, box-shadow 160ms ease, background 160ms ease",
        }}
      >
        <div className="flex items-center gap-3">
          <div
            className={`relative h-10 w-10 shrink-0 ${isRefreshing ? "animate-spin" : ""}`}
            style={{
              transform: `scale(${ringScale})`,
              transition: "transform 140ms ease",
            }}
          >
            <svg width="40" height="40" viewBox="0 0 40 40" className="block">
              <circle
                cx="20"
                cy="20"
                r={ringRadius}
                fill="rgba(184,115,51,0.08)"
                stroke="rgba(184,115,51,0.18)"
                strokeWidth="2"
              />
              <circle
                cx="20"
                cy="20"
                r={ringRadius}
                fill="none"
                stroke={COPPER}
                strokeWidth="2.75"
                strokeLinecap="round"
                strokeDasharray={ringCircumference}
                strokeDashoffset={isRefreshing ? ringCircumference * 0.22 : ringOffset}
                transform="rotate(-90 20 20)"
              />
            </svg>
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-zinc-100">{label}</div>
            <div className="text-[11px] text-zinc-400">{sublabel}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [isAdmin, setIsAdmin] = useState(false);
  const [pendingApplicationsCount, setPendingApplicationsCount] = useState(0);
  const [viewer, setViewer] = useState({
    name: "",
    photoUrl: "",
    role: "member",
    status: "",
    email: "",
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  const contentRef = useRef(null);
  const touchStartYRef = useRef(0);
  const canPullRef = useRef(false);
  const pullDistanceRef = useRef(0);

  const [pullDistance, setPullDistance] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    pullDistanceRef.current = pullDistance;
  }, [pullDistance]);

  useEffect(() => {
    const run = async () => {
      const user = auth.currentUser;
      if (!user) {
        setIsAdmin(false);
        setPendingApplicationsCount(0);
        setViewer({ name: "", photoUrl: "", role: "member", status: "", email: "" });
        return;
      }

      try {
        const userRef = doc(db, "users", user.uid);
        const snap = await getDoc(userRef);
        const data = snap.exists() ? snap.data() : {};
        const role = String(data?.role || "member");
        const admin = role === "admin";

        setIsAdmin(admin);
        setViewer({
          name: String(data?.name || data?.displayName || user.displayName || "").trim(),
          photoUrl: String(data?.profilePhotoUrl || "").trim(),
          role,
          status: String(data?.status || "").trim(),
          email: String(user.email || data?.email || "").trim(),
        });

        if (admin) {
          try {
            const appsQuery = query(
              collection(db, "applications"),
              where("status", "==", "pending"),
              orderBy("submittedAt", "asc")
            );
            const appsSnap = await getDocs(appsQuery);
            setPendingApplicationsCount(appsSnap.size);
          } catch (err) {
            console.error("Failed to load pending applications count:", err);
            setPendingApplicationsCount(0);
          }
        } else {
          setPendingApplicationsCount(0);
        }
      } catch (error) {
        console.error("Failed to load layout user:", error);
        setIsAdmin(false);
        setPendingApplicationsCount(0);
      }
    };

    run();
  }, [location.pathname]);

  useEffect(() => {
    setMobileOpen(false);
    setPullDistance(0);
    setIsPulling(false);
    setIsRefreshing(false);
    canPullRef.current = false;
    pullDistanceRef.current = 0;
  }, [location.pathname]);

  const handleLogout = async () => {
    await auth.signOut();
    navigate("/login");
  };

  const triggerRefresh = useCallback(() => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    setIsPulling(false);
    setPullDistance(68);

    if (contentRef.current) {
      contentRef.current.scrollTo({ top: 0, behavior: "auto" });
    }

    window.setTimeout(() => {
      window.location.reload();
    }, 430);
  }, [isRefreshing]);

  const resetPullState = useCallback(() => {
    canPullRef.current = false;
    setIsPulling(false);
    setPullDistance(0);
  }, []);

  const handleTouchStart = useCallback(
    (event) => {
      if (!isMobileTouchDevice() || isRefreshing || mobileOpen) return;
      const container = contentRef.current;
      if (!container || container.scrollTop > 0) {
        canPullRef.current = false;
        return;
      }

      const touch = event.touches?.[0];
      if (!touch) return;

      touchStartYRef.current = touch.clientY;
      canPullRef.current = true;
      setIsPulling(false);
    },
    [isRefreshing, mobileOpen]
  );

  const handleTouchMove = useCallback(
    (event) => {
      if (!canPullRef.current || isRefreshing || mobileOpen) return;
      const container = contentRef.current;
      if (!container) return;

      if (container.scrollTop > 0) {
        resetPullState();
        return;
      }

      const touch = event.touches?.[0];
      if (!touch) return;

      const delta = touch.clientY - touchStartYRef.current;
      if (delta <= 0) {
        setIsPulling(false);
        setPullDistance(0);
        return;
      }

      const dampened = delta <= 120 ? delta * 0.48 : 57.6 + (delta - 120) * 0.16;
      const nextDistance = Math.min(PULL_MAX, dampened);
      setIsPulling(true);
      setPullDistance(nextDistance);

      if (typeof event.preventDefault === "function") {
        event.preventDefault();
      }
    },
    [isRefreshing, mobileOpen, resetPullState]
  );

  const handleTouchEnd = useCallback(() => {
    if (!canPullRef.current || isRefreshing) return;
    canPullRef.current = false;
    setIsPulling(false);

    if (pullDistanceRef.current >= PULL_TRIGGER) {
      triggerRefresh();
      return;
    }

    setPullDistance(0);
  }, [isRefreshing, triggerRefresh]);

  const handleTouchCancel = useCallback(() => {
    if (isRefreshing) return;
    resetPullState();
  }, [isRefreshing, resetPullState]);

  const navItemClass = (path) => {
    const active = location.pathname === path;
    return `block w-full list-none px-3 py-2 rounded transition-colors hover:bg-zinc-800 hover:text-[#B87333] ${
      active ? "bg-zinc-800 text-[#B87333]" : "text-zinc-100"
    }`;
  };

  const MemberHeader = ({ compact = false, onNavigate }) => {
    const name = viewer.name || "Member";
    const role = viewer.role || (isAdmin ? "admin" : "member");
    const status = viewer.status || "active";
    const roleTone = role === "admin" ? "copper" : "neutral";
    const statusTone = String(status).toLowerCase() === "suspended" ? "red" : "green";

    return (
      <Link
        to="/account"
        onClick={onNavigate}
        className={`w-full list-none rounded-xl border border-zinc-800 bg-zinc-950/40 hover:bg-zinc-950/70 transition-colors ${
          compact ? "p-3" : "p-4"
        }`}
        style={{ borderColor: "rgba(184,115,51,0.18)" }}
        title="Go to My Account"
      >
        <div className="flex items-center gap-3 list-none">
          <Avatar name={name} photoUrl={viewer.photoUrl} size={compact ? 40 : 44} />
          <div className="min-w-0 flex-1 list-none">
            <div className="text-zinc-100 font-semibold truncate list-none">{name}</div>
            <div className="mt-1 flex flex-wrap items-center gap-2 list-none">
              <Badge tone={roleTone}>{role === "admin" ? "Admin" : "Member"}</Badge>
              <Badge tone={statusTone}>
                {String(status).toLowerCase() === "suspended" ? "Suspended" : "Active"}
              </Badge>
            </div>
          </div>
          <div className="text-zinc-400 shrink-0 list-none">›</div>
        </div>
      </Link>
    );
  };

  const PendingApplicationsNavItem = ({ onNavigate }) => {
    const active = location.pathname === "/admin/pending-applications";

    return (
      <li className="list-none">
        <Link
          to="/admin/pending-applications"
          className={`flex items-center justify-between w-full list-none px-3 py-2 rounded transition-colors hover:bg-zinc-800 hover:text-[#B87333] ${
            active ? "bg-zinc-800 text-[#B87333]" : "text-zinc-100"
          }`}
          onClick={onNavigate}
        >
          <span>Pending Applications</span>
          {pendingApplicationsCount > 0 ? (
            <span className="inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full text-[11px] font-bold bg-red-600 text-white">
              {pendingApplicationsCount}
            </span>
          ) : null}
        </Link>
      </li>
    );
  };

  const NavLinks = ({ onNavigate }) => (
    <ul className="flex-1 space-y-3 list-none p-0 m-0">
      <li className="list-none">
        <Link to="/dashboard" className={navItemClass("/dashboard")} onClick={onNavigate}>
          Dashboard
        </Link>
      </li>
      <li className="list-none">
        <Link to="/tournaments" className={navItemClass("/tournaments")} onClick={onNavigate}>
          Tournaments
        </Link>
      </li>
      <li className="list-none">
        <Link to="/cash-games" className={navItemClass("/cash-games")} onClick={onNavigate}>
          Cash Games
        </Link>
      </li>
      <li className="list-none">
        <Link to="/account" className={navItemClass("/account")} onClick={onNavigate}>
          My Account
        </Link>
      </li>

      {isAdmin && (
        <>
          <li className="pt-2 list-none">
            <div className="text-xs uppercase tracking-wider text-zinc-500 px-2">Admin</div>
          </li>
          <li className="list-none">
            <Link to="/admin/users" className={navItemClass("/admin/users")} onClick={onNavigate}>
              Users
            </Link>
          </li>
          <PendingApplicationsNavItem onNavigate={onNavigate} />
          <li className="list-none">
            <Link
              to="/admin/communications"
              className={navItemClass("/admin/communications")}
              onClick={onNavigate}
            >
              Communications
            </Link>
          </li>
          <li className="list-none">
            <Link
              to="/admin/cash-games"
              className={navItemClass("/admin/cash-games")}
              onClick={onNavigate}
            >
              Cash Games
            </Link>
          </li>
          <li className="list-none">
            <Link
              to="/blind-structures"
              className={navItemClass("/blind-structures")}
              onClick={onNavigate}
            >
              Blind Structures
            </Link>
          </li>
          <li className="list-none">
            <Link to="/chip-sets" className={navItemClass("/chip-sets")} onClick={onNavigate}>
              Chip Sets
            </Link>
          </li>
        </>
      )}

      <li className="pt-2 list-none">
        <button
          onClick={() => {
            if (onNavigate) onNavigate();
            handleLogout();
          }}
          className="w-full text-left px-3 py-2 rounded transition-colors hover:bg-zinc-800 hover:text-[#B87333] text-zinc-100"
        >
          Logout
        </button>
      </li>
    </ul>
  );

  return (
    <div className="min-h-screen md:h-screen md:overflow-hidden bg-zinc-950 list-none">
      <div className="md:hidden sticky top-0 z-40 bg-zinc-950 border-b border-zinc-800">
        <div className="h-14 px-4 flex items-center justify-between">
          <button
            onClick={() => setMobileOpen(true)}
            className="inline-flex items-center justify-center h-10 w-10 rounded bg-zinc-900 border border-zinc-800 hover:border-zinc-700 text-zinc-100"
            aria-label="Open menu"
          >
            ☰
          </button>

          <div className="flex items-center gap-3 min-w-0">
            <img src={mobileLogo} alt="River Ranch Poker Club" className="h-8 w-auto object-contain" />
            <div className="truncate text-sm font-semibold text-zinc-100">River Ranch Poker Club</div>
          </div>

          <Link to="/account" className="h-10 w-10 flex items-center justify-center list-none" aria-label="My Account">
            <Avatar name={viewer.name || "Member"} photoUrl={viewer.photoUrl} size={32} />
          </Link>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-black/70" onClick={() => setMobileOpen(false)} />
          <div className="list-none absolute inset-y-0 left-0 w-72 max-w-[85vw] bg-zinc-900 border-r border-zinc-800 p-5 flex flex-col">
            <div className="flex items-center justify-between mb-4 list-none">
              <img src={mobileLogo} alt="River Ranch Poker Club" className="h-14 w-auto object-contain list-none" />
              <button
                onClick={() => setMobileOpen(false)}
                className="inline-flex items-center justify-center h-10 w-10 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-100 list-none"
                aria-label="Close menu"
              >
                ✕
              </button>
            </div>
            <div className="mb-4 list-none">
              <MemberHeader compact onNavigate={() => setMobileOpen(false)} />
            </div>
            <NavLinks onNavigate={() => setMobileOpen(false)} />
            <div className="text-xs text-zinc-500 pt-4 border-t border-zinc-800 list-none">
              River Ranch Poker Club
            </div>
          </div>
        </div>
      )}

      <div className="min-h-screen md:min-h-0 md:h-full flex list-none">
        <div className="hidden md:flex w-64 min-h-0 overflow-y-auto bg-zinc-900 text-zinc-100 p-6 flex-col border-r border-zinc-800 list-none">
          <img src={logo} alt="River Ranch Poker Club" className="h-20 w-auto object-contain mb-5 list-none" />
          <div className="mb-5 list-none">
            <MemberHeader />
          </div>
          <NavLinks />
          <div className="text-xs text-zinc-500 list-none">River Ranch Poker Club</div>
        </div>

        <div
          ref={contentRef}
          className="flex-1 min-w-0 min-h-0 overflow-y-auto relative list-none"
          style={{
            overscrollBehaviorY: "contain",
            WebkitOverflowScrolling: "touch",
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchCancel}
        >
          <PullToRefreshIndicator pullDistance={pullDistance} isRefreshing={isRefreshing} />
          <div
            className="min-h-full p-4 md:p-6 list-none"
            style={{
              transform: `translateY(${pullDistance}px)`,
              willChange: pullDistance > 0 ? "transform" : "auto",
              transition:
                isPulling || isRefreshing
                  ? "none"
                  : "transform 240ms cubic-bezier(0.22, 1, 0.36, 1)",
            }}
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
